import { useQueryClient } from '@tanstack/react-query';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Alert, Box, Button } from '@mui/material';
import Dialog from 'components/dialog';
import FlexBox from 'components/flex-box';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';

import { useAccountState } from 'contexts/account/account-context';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';

import { Feed } from 'views/self-service/features/feed-catalog/types/feed.type';

import NumberField from 'components/number-field';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { getBrand } from '../api/get-brand';
import { postFeed } from '../api/post-feed';
import { CreateFeedInput } from '../types/create-feed-input.type';
import { FeedSubType } from '../types/feed-sub-type.enum';
import { FeedType } from '../types/feed-type.enum';

type CreateFeedProps = {
  feedCodes: string[];
};

type FormValues = {
  code: string | null;
  feedBrandId: number | string;
  name: string;
  size: number | string;
  price: number | string;
  feedType: string;
  feedSubType: string;
};

const PAGE_NAME_ID = 'selfService.feedCatalog';

const CreateFeed: React.FC<CreateFeedProps> = ({ feedCodes }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);

  const [duplicatedCode, setDuplicatedCode] = useState(false);

  //#region FORMS
  const { handleSubmit, control, getValues, setError, clearErrors } = useForm<FormValues>({
    defaultValues: {
      name: '',
      size: '',
      price: '',
      feedType: '',
      feedSubType: '',
      feedBrandId: '',
      code: '',
    },
    shouldUnregister: true,
  });
  //#endregion FORMS

  //#region FETCH DATA
  const { data: allBrands } = useGet(['getBrand'], () => getBrand(account.company!.companyId), {
    enabled: !!account.company!.companyId,
    select: (data) =>
      data.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          numeric: true,
        })
      ),
  });
  //#endregion FETCH DATA

  //#region POST DATA
  const createFeed = usePost<Feed, { request: CreateFeedInput }>(
    (request) => postFeed(account.company!.companyId, request.request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      [['getFeed'], ['getFeeds']].forEach((queryKey) => {
        queryClient.invalidateQueries({
          queryKey: queryKey,
        });
      });
      dialogRef?.current?.setOpen(false);
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  //#endregion POST DATA

  //#region HANDLERS

  const handleCreateFeed = (request: FormValues) => {
    createFeed.mutate({
      request: {
        ...request,
        code: request.code?.trim() === '' ? null : request.code,
        size: Number(request.size),
        price: Number(request.price),
        feedBrandId: Number(request.feedBrandId),
      } as CreateFeedInput,
    });
  };

  const onKeyDown = (e: any) => e.stopPropagation();

  const handleCodeError = (isDuplicate: boolean) => {
    setDuplicatedCode(isDuplicate);
    isDuplicate ? setError('code', {}) : clearErrors('code');
  };

  //#endregion HANDLERS

  return (
    <Box width={{ xs: '100%', md: 'auto' }} flexDirection={'column'} display={'flex'} gap={3} mb={3}>
      <Dialog
        maxWidth="sm"
        ref={dialogRef}
        sx={{ zIndex: 1510 }}
        onClose={() => handleCodeError(false)}
        onKeyDown={(e) => e.key === 'Tab' && e.stopPropagation()}
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.createNewFeed` })}
        button={<Button fullWidth>{intl.formatMessage({ id: `${PAGE_NAME_ID}.createNewFeed` })}</Button>}>
        <>
          <form onSubmit={handleSubmit(handleCreateFeed)}>
            {createFeed.isPending && <LoadingSpinner overlay />}
            <FlexBox>
              {duplicatedCode && (
                <Alert severity="warning">
                  <FormattedMessage id={`${PAGE_NAME_ID}.createFeedCatalog.dialog.duplicatedCode`} />
                </Alert>
              )}
              <TextField
                dark
                fullWidth
                stopPropagation
                onKeyDown={onKeyDown}
                controller={{
                  name: 'name',
                  control: control,
                  rules: {
                    required: intl.formatMessage({ id: 'form.rules.required' }),
                  },
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeedCatalog.dialog.inputName` })}
              />
              <TextField
                dark
                fullWidth
                maxLength={50}
                onBlur={() => handleCodeError(feedCodes.includes(getValues('code') ?? ''))}
                controller={{
                  name: 'code',
                  control: control,
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeedCatalog.dialog.inputCode` })}
              />
              <Select
                dark
                nested
                fullWidth
                stopPropagation
                controller={{
                  name: 'feedBrandId',
                  control: control,
                  rules: {
                    required: intl.formatMessage({ id: 'form.rules.required' }),
                    validate: {
                      required: (v) => (parseInt(v) > 0 ? true : intl.formatMessage({ id: 'form.rules.required' })),
                    },
                  },
                }}
                noResults={{
                  show: !allBrands?.length,
                  text: <FormattedMessage id={'general.noResults'} />,
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeedCatalog.dialog.selectFeedBrand` })}>
                {allBrands?.map((brand) => (
                  <RadioSelectItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </RadioSelectItem>
                ))}
              </Select>
              <Box gap={4} display={'flex'} flexDirection={'row'}>
                <Select
                  dark
                  nested
                  fullWidth
                  stopPropagation
                  controller={{
                    name: 'feedType',
                    control: control,
                    rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                  }}
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeedCatalog.dialog.selectFeedType` })}>
                  {Object.values(FeedType).map((value, index) => (
                    <RadioSelectItem key={index} value={value}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.types.${value}`} />
                    </RadioSelectItem>
                  ))}
                </Select>
                <Select
                  dark
                  fullWidth
                  nested
                  controller={{
                    name: 'feedSubType',
                    control: control,
                    rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
                  }}
                  label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeedCatalog.dialog.selectFeedSubType` })}>
                  {Object.values(FeedSubType).map((value, index) => (
                    <RadioSelectItem key={index} value={value}>
                      <FormattedMessage id={`${PAGE_NAME_ID}.types.${value}`} />
                    </RadioSelectItem>
                  ))}
                </Select>
              </Box>
              <NumberField
                dark
                fullWidth
                onKeyDown={onKeyDown}
                controller={{
                  name: 'size',
                  control: control,
                  rules: {
                    required: intl.formatMessage({ id: 'form.rules.required' }),
                    validate: {
                      positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                    },
                  },
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeedCatalog.dialog.inputSize` })}
              />
              <NumberField
                dark
                fullWidth
                onKeyDown={onKeyDown}
                controller={{
                  name: 'price',
                  control: control,
                  rules: {
                    required: intl.formatMessage({ id: 'form.rules.required' }),
                    validate: {
                      positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                    },
                  },
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.createFeedCatalog.dialog.inputPrice` })}
              />
              <Box display={'flex'} justifyContent={'end'}>
                <Button type="submit" disabled={duplicatedCode}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.createFeedCatalog.dialog.button`} />
                </Button>
              </Box>
            </FlexBox>
          </form>
        </>
      </Dialog>
    </Box>
  );
};

export default React.memo(CreateFeed);
